import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { COMMUNITY_API } from '@app/app.token';
import { CommunityProfile, CommunityProfileWithBranches } from '@models/community-profile';
import { catchError, map, Observable, of, zip } from 'rxjs';

@Injectable( {
    providedIn: 'root'
} )
export class CommunityProfileService {
    /** ignore in coverage */
    #apiUrl = inject( COMMUNITY_API );
    #httpClient = inject( HttpClient );

    /** get community profile details */
    getCommunityProfile( communityId: string ): Observable<CommunityProfile | null> {
        return this.#httpClient
            .get<CommunityProfile | null>( `${this.#apiUrl}/companies/${communityId}` )
            .pipe(
                map( profile => {
                    if ( !profile ) return profile;

                    const locs = profile?.locations as any[] | undefined;
                    const hasRealLocations = Array.isArray( locs ) &&
                        locs.length > 0 &&
                        locs[ 0 ]?.coordinates !== undefined;

                    if ( !hasRealLocations ) {
                        // Fallback: companyAddress als Hauptniederlassung
                        ( profile as any ).locations = profile.companyAddress ? [ {
                            street: profile.companyAddress.street,
                            city: profile.companyAddress.city,
                            zip: profile.companyAddress.zip,
                            countryCode: profile.companyAddress.countryCode,
                            isHeadquarter: true,
                            coordinates: profile.coordinates
                        } ] : [];
                    }
                    return profile;
                } ),
                catchError( () => of( null ) )
            );
    }

    /**
     * get community profile with all branches in one call
     * uses /with-branches endpoint which returns { company, branches }
     *
     * error handling:
     * - 404: no consent / membership — load company only, branches empty
     * - 403: registrar denies access (e.g. backend internal 403) — load company only, branches empty
     * - 500 or other: fallback to separate /companies and /branches calls
     */
    getCommunityProfileWithBranches( communityId: string ): Observable<CommunityProfileWithBranches | null> {
        return this.#httpClient
            .get<CommunityProfileWithBranches>( `${this.#apiUrl}/companies/${communityId}/with-branches` )
            .pipe(
                map( response => response ?? null ),
                catchError( ( error: HttpErrorResponse ) => {
                    if ( error.status === 404 || error.status === 403 ) {
                        // 404: Kein Consent (Community-Membership) -> Branches werden vom Backend
                        // nicht freigegeben. Company-Profil selbst ist davon nicht betroffen
                        // (eigener Endpoint, kein Consent-Check) und wird weiterhin angezeigt;
                        // nur die Branches-Liste bleibt leer.
                        // 403: Registrar verweigert dem Community Service den Zugriff auf Branches
                        // (interner Backend-Fehler) — Company-Profil trotzdem laden, Branches leer.
                        return this.#httpClient.get<CommunityProfile>( `${this.#apiUrl}/companies/${communityId}` ).pipe(
                            map( company => company ? { company, branches: [] } : null ),
                            catchError( () => of( null ) )
                        );
                    }

                    // Fallback: einzelne Calls nur bei echten technischen Fehlern
                    // (z.B. with-branches Endpoint nicht erreichbar/nicht deployed)
                    const company$ = this.#httpClient.get<CommunityProfile>( `${this.#apiUrl}/companies/${communityId}` );
                    const branches$ = this.#httpClient.get<CommunityProfile[]>( `${this.#apiUrl}/companies/${communityId}/branches` ).pipe(
                        // guard: 500 wirft einen Error der den zip abbricht — catchError fängt das ab
                        // branches ?? [] im zip greift nur bei null/undefined, nicht bei HTTP Errors
                        catchError( () => of( [] ) )
                    );

                    return zip( company$, branches$ ).pipe(
                        map( ( [ company, branches ] ) => company ? { company, branches } : null ),
                        catchError( () => of( null ) )
                    );
                } )
            );
    }

    /** save community profile details */
    saveCompanyProfile( profileData: CommunityProfile ): Observable<CommunityProfile | null> {
        return this.#httpClient
            .post<CommunityProfile | null>( `${this.#apiUrl}/companies`, profileData )
            .pipe(
                map( ( response: CommunityProfile | null ) => response ),
                catchError( () => of( null ) )
            );
    }

    /** update community profile details */
    updateCompanyProfile( profileData: CommunityProfile ) {
        return this.#httpClient
            .put( `${this.#apiUrl}/companies/${profileData.communityId}`, profileData )
            .pipe(
                map( ( response ) => response ),
                catchError( () => of( null ) )
            );
    }

    /** load company logo from blob storage */
    getCompanyLogo( communityId: string ) {
        return this.#httpClient
            .get( `${this.#apiUrl}/companyLogo/${communityId}`, { responseType: 'blob' } )
            .pipe( map( ( response ) => response ) );
    }

    /** delete company logo from blob storage */
    deleteCompanyLogo( communityId: string ) {
        return this.#httpClient
            .delete( `${this.#apiUrl}/companyLogo/${communityId}` )
            .pipe( map( ( response ) => response ) );
    }

    /** upload company logo into blob storage */
    uploadCompanyLogo( file: File, communityId: string ) {
        const headers = new HttpHeaders();
        headers.set( 'Content-Type', 'multipart/form-data' );

        const formData = new FormData();
        formData.append( 'fileData', file );
        formData.append( 'fileName', file.name );
        formData.append( 'communityId', communityId );

        return this.#httpClient
            .post( `${this.#apiUrl}/companyLogo`, formData, { observe: 'response' } )
            .pipe( map( ( response ) => response ) );
    }

}
